import React, { FC } from 'react';
import {
    SkeletonBlock,
    SkeletonDivider,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonResponsiveGrid,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';
import styled from 'styled-components';

// Mirrors SectionWithLeftContent: title and description on the left, the controls on the right.
const SectionRow = styled.div`
    display: flex;
    gap: 30px;
    width: 100%;
    min-width: 0;

    @media (max-width: 900px) {
        flex-direction: column;
    }
`;

const SectionLeft = styled(SkeletonStack)`
    flex: 0 0 20%;
    min-width: 0;
`;

const SectionRight = styled(SkeletonStack)`
    flex: 1 1 auto;
    min-width: 0;
`;

const LeftContentSkeleton: FC<{ $titleWidth: string }> = ({ $titleWidth }) => (
    <SectionLeft $gap='10px'>
        <SkeletonBlock $width={$titleWidth} $height='18px' />
        <SkeletonStack $gap='8px'>
            <SkeletonBlock $width='100%' $height='12px' />
            <SkeletonBlock $width='88%' $height='12px' />
            <SkeletonBlock $width='64%' $height='12px' />
        </SkeletonStack>
    </SectionLeft>
);

// One starting experience card: the illustration, then the radio with its label and caption.
const StartingExperienceCardSkeleton: FC = () => (
    <SkeletonSurface $padding='14px'>
        <SkeletonStack $gap='16px'>
            <SkeletonBlock $height='135px' $radius='10px' />
            <SkeletonRow $gap='10px' $align='flex-start'>
                <SkeletonBlock $width='18px' $height='18px' $radius='999px' />
                <SkeletonStack $gap='8px' $flex='1'>
                    <SkeletonBlock $width='150px' $height='15px' />
                    <SkeletonBlock $width='95%' $height='12px' />
                    <SkeletonBlock $width='70%' $height='12px' />
                </SkeletonStack>
            </SkeletonRow>
        </SkeletonStack>
    </SkeletonSurface>
);

export const DesignWithAiPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true' $padding='20px 0 92px'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonRow $gap='10px'>
                    <SkeletonBlock $width='230px' $height='28px' />
                    <SkeletonBlock $width='82px' $height='22px' $radius='999px' />
                    <SkeletonBlock $width='64px' $height='22px' $radius='999px' />
                </SkeletonRow>
                <SkeletonBlock $width='min(680px, 100%)' $height='13px' />
                <SkeletonBlock $width='90px' $height='13px' />
            </SkeletonStack>
        </SkeletonPageHeader>
        <SkeletonStack $gap='20px'>
            {/* The switch card, with the AI credits banner inside it */}
            <SkeletonSurface $padding='24px'>
                <SkeletonStack $gap='18px'>
                    <SkeletonRow $gap='12px' $align='flex-start'>
                        <SkeletonBlock $width='42px' $height='22px' $radius='999px' />
                        <SkeletonStack $gap='8px' $flex='1'>
                            <SkeletonBlock $width='min(200px, 70%)' $height='16px' />
                            <SkeletonBlock $width='min(380px, 90%)' $height='12px' />
                        </SkeletonStack>
                    </SkeletonRow>
                    <SkeletonBlock $height='58px' $radius='6px' />
                </SkeletonStack>
            </SkeletonSurface>
            {/* Customer Experience: starting experience cards, then the interaction mode */}
            <SkeletonSurface $padding='22px 24px'>
                <SectionRow>
                    <LeftContentSkeleton $titleWidth='150px' />
                    <SectionRight $gap='20px'>
                        <SkeletonStack $gap='12px'>
                            <SkeletonStack $gap='6px'>
                                <SkeletonBlock $width='140px' $height='14px' />
                                <SkeletonBlock $width='min(360px, 100%)' $height='12px' />
                            </SkeletonStack>
                            <SkeletonResponsiveGrid $columns='repeat(2, minmax(0, 1fr))' $tabletColumns='repeat(2, minmax(0, 1fr))' $gap='20px'>
                                <StartingExperienceCardSkeleton />
                                <StartingExperienceCardSkeleton />
                            </SkeletonResponsiveGrid>
                        </SkeletonStack>
                        <SkeletonDivider />
                        <SkeletonStack $gap='12px'>
                            <SkeletonStack $gap='6px'>
                                <SkeletonBlock $width='120px' $height='14px' />
                                <SkeletonBlock $width='min(420px, 100%)' $height='12px' />
                            </SkeletonStack>
                            <SkeletonBlock $height='38px' $radius='6px' />
                        </SkeletonStack>
                    </SectionRight>
                </SectionRow>
            </SkeletonSurface>
            {/* Usage controls */}
            <SkeletonSurface $padding='22px 24px'>
                <SectionRow>
                    <LeftContentSkeleton $titleWidth='120px' />
                    <SectionRight $gap='16px'>
                        {Array.from({ length: 2 }).map((_, index) => (
                            <SkeletonRow key={index} $gap='14px'>
                                <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                <SkeletonStack $gap='6px' $flex='1'>
                                    <SkeletonBlock $width='min(200px, 60%)' $height='14px' />
                                    <SkeletonBlock $width='min(360px, 90%)' $height='12px' />
                                </SkeletonStack>
                            </SkeletonRow>
                        ))}
                    </SectionRight>
                </SectionRow>
            </SkeletonSurface>
        </SkeletonStack>
        <SkeletonFooterBar>
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
        </SkeletonFooterBar>
    </SkeletonPageShell>
);
